import { useEffect, useState } from "react"
import styled from "styled-components"
import { FaExclamationTriangle } from "react-icons/fa"
import moment from "moment"
import { checkHasPermissionShipmentTrackingRead, checkHasPermissionShipmentTrackingEdit } from "../../utils"
import useFetchOpportunities from "../Shared/useFetchOpportunities"
import LineItems from "./LineItems"
import RecipientDetails from "./RecipientDetails"
import OpportunityDetails from "./OpportunityDetails"
import ManageOpportunityButton from "./ManageOpportunityButton"
import CreateOrderButton, { CreateOrderModal } from "../Orders/CreateOrderButton/CreateOrderButton"
import { useDownloadOpportunity } from "./DownloadOpportunityButton"
import { ImportOpportunityModal } from "./ImportOpportunityButton"
import { useRefreshOpportunity } from "./RefreshOpportunityButton"
import EllipsisMenu, { EllipsisMenuItem } from "../Shared/EllipsisMenu"
import ShippingNotes from "./ShippingNotes"
import { OpportunityShareModal } from "./OpportunityShareButton"

const Card = styled.div`
  background: white;
  border: 1px solid #dedede;
  border-radius: 5px;
  box-shadow: 2px 2px 6px rgb(0 0 0 / 15%);
  margin: 2px 4px 4px 2px;

  &.closed {
    color: #080808;
  }
`

const Header = styled.div`
  background: #f8f9fc;
  border-bottom: 1px solid #dedede;
  padding: 10px 20px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  display: flex;
  flex-direction: row;
  font-size: 12px;
  font-weight: bold;
`

const HeaderRow = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
`

const Body = styled.div`
  padding: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
`

export default function OpportunityCard ({
  opportunity,
  showManageButton = true,
  showRefreshButton = true,
  maxLineItemsHeight,
  onCreateOrder,
  onOpportunityUpdate,
  shipModals
}) {
  const {
    Id,
    Account,
    Name,
    OpportunityLineItems,
    CloseDate, 
    Probability,
    PO_Number__c,
    Created_Date_Formula__c,
    Has_Had_QFC_Boxes_Requested__c,
    Order_Flagged__c
  } = opportunity

  const { shipModalsChange } = shipModals || {}

  const is_closed = (!!CloseDate)
  const maxLineItemsStyles = (maxLineItemsHeight ? { maxHeight: maxLineItemsHeight, overflow: 'hidden auto' } : {})

  const hasRead = checkHasPermissionShipmentTrackingRead()
  const hasEdit = checkHasPermissionShipmentTrackingEdit()

  const [isCreateOrderQfcOpen, setIsCreateOrderQfcOpen] = useState(false)
  const [isImportOppOpen, setIsImportOppOpen] = useState(false)
  const [isShareOppOpen, setIsShareOppOpen] = useState(false)

  const downloadOpportunity = useDownloadOpportunity()
  const refreshOpportunityHook = useRefreshOpportunity()
  const { fetchOpportunities } = useFetchOpportunities()

  const refreshOpportunity = async () => {
    await refreshOpportunityHook(Id)
    const opps = await fetchOpportunities({ opportunityId: Id })
    onOpportunityUpdate?.(opps?.[0])
  }

  const shareOpportunity = (isOpen) => {
    setIsShareOppOpen(isOpen)
  }

  const formatDate = () => {
    const displayDate = CloseDate || Created_Date_Formula__c
    return displayDate ? `${CloseDate ? 'Close Date' : 'Created On'} ${moment(displayDate).format('MM/DD/YYYY')}` : '[no date found]'
  }

  useEffect(() => {
    const { manageOppOpportunity } = shipModalsChange || {}
    if (Id === manageOppOpportunity?.Id) {
      refreshOpportunity()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipModalsChange])

  return (
    
    <Card className={is_closed && 'closed'}>
      <Header>
        <HeaderRow>
          {Order_Flagged__c && 
            <div style={{ cursor: 'help', marginRight: '15px' }}>
              <FaExclamationTriangle 
                title={'One or more orders have been flagged for having delivery issues'} 
                style={{ color: '#ef0c0c', fontSize: '20px' }} 
              />
            </div>
          }
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '16px', marginRight: '5px' }}>{Name ? Name : 'No Account'}</div>
            {PO_Number__c &&
              <div style={{ fontStyle: 'italic', fontWeight: 'normal', color: (!!is_closed ? '#565656' : 'inherit') }}>PO #{PO_Number__c}</div>
            }
            <div style={{ fontStyle: 'italic', fontWeight: 'normal', color: (!!is_closed ? '#565656' : 'inherit') }}>{formatDate()}</div>
            <div style={{ fontStyle: 'italic', fontWeight: 'normal', color: (!!is_closed ? '#565656' : 'inherit') }}>Probability {Probability}%</div>
          </div>
          <div style={{ flex: 1, display: 'flex', justifyContent: 'end', gap: '10px' }}>
            {showManageButton && hasRead &&
              <ManageOpportunityButton opportunity={opportunity} shipModals={shipModals} />
            }
            {!showManageButton && hasRead && <>
              <CreateOrderButton opportunityId={opportunity.Id} updateOrderData={onCreateOrder} />
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <EllipsisMenu>
                  {!Has_Had_QFC_Boxes_Requested__c && hasEdit &&
                    <EllipsisMenuItem name="Training" onClick={() => setIsCreateOrderQfcOpen(true)} />
                  }
                  <EllipsisMenuItem name="Export" onClick={() => downloadOpportunity(opportunity)} />
                  {hasEdit && 
                    <EllipsisMenuItem name="Import" onClick={() => setIsImportOppOpen(true)} />
                  }
                  {showRefreshButton &&
                    <EllipsisMenuItem name="Refresh" onClick={() => refreshOpportunity(opportunity.Id)} />
                  }
                  <EllipsisMenuItem name="Share" onClick={() => shareOpportunity(true)} />
                </EllipsisMenu>
                {isImportOppOpen &&
                  <ImportOpportunityModal onClose={() => setIsImportOppOpen(false)} opportunity={opportunity} onCreateOrders={onCreateOrder} />
                }
                {isCreateOrderQfcOpen && 
                  <CreateOrderModal
                    onClose={() => setIsCreateOrderQfcOpen(false)} 
                    opportunityId={opportunity.Id} 
                    updateOrderData={onCreateOrder} 
                    isOpportunityQfc={true} 
                  />
                }
              </div>
            </>}
          </div>
        </HeaderRow>
      </Header>
      
      <Body style={{ ...maxLineItemsStyles }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px', fontSize: '14px' }}>
          <OpportunityDetails opportunity={opportunity} />
          <RecipientDetails account={Account} />
          <ShippingNotes opportunity={opportunity} onOpportunityUpdate={onOpportunityUpdate} />
        </div>
        <div style={{ flex: 1 }}>
          <LineItems lineItems={OpportunityLineItems?.records} />
        </div>
      </Body>

      {isShareOppOpen &&
        <OpportunityShareModal opportunity={opportunity} onClose={() => shareOpportunity(false)} />
      }
    </Card>
  )
}